import { AxiosResponse } from "axios";
import axiosInstance from "../api/axiosInstance";
import {
  TAutoShareResponse,
  TClientForm,
  TClientListResponse,
  TClientPageParams,
  TClientPageResponse,
  TClientShare,
} from "../types/index.type";

const createClient = async (client: TClientForm) => {
  return axiosInstance.post(`/client/`, client);
};

const getAllClients = async (): Promise<AxiosResponse<TClientListResponse>> => {
  return axiosInstance.get(`/client/list/`);
};

// Paginated + searchable variant of the same endpoint. Passing params switches the backend from the
// legacy {owned, shared} payload to {results, hasMore, next_offset}. `signal` lets callers cancel a
// stale in-flight search (AbortController) so an out-of-order response can't repaint the wrong list.
const getClientsPaged = async (
  params: TClientPageParams = {},
  signal?: AbortSignal,
): Promise<AxiosResponse<TClientPageResponse>> => {
  return axiosInstance.get(`/client/list/`, { params, signal });
};

const updateClient = (clientId: number, client: TClientForm) => {
  return axiosInstance.put(`/client/${clientId}/`, client);
};

const deleteClient = (clientId: number) => {
  return axiosInstance.delete(`/client/${clientId}/`);
};

const getClientDetails = (clientId: number) => {
  return axiosInstance.get(`/client/${clientId}/`);
};

const getUsersClientProgressSummary = (clientId: number) => {
  return axiosInstance.post(`/client/progress_summary/`, {
    id: clientId,
  });
};

const getAllDcmCodes = async () => {
  return axiosInstance.get(`/client/dsm/`);
};

const validateClientName = (clientForm: TClientForm, referIndividualBy: string) => {
  // Check if the client name field is empty
  if (clientForm.name === "") {
    return `Please enter ${referIndividualBy.toLowerCase()} name`;
  }

  // Check for special characters in the name and ensure it starts with a letter
  const regex = /^[a-zA-Z][a-zA-Z0-9\s.,'-]*$/;
  if (!regex.test(clientForm.name.trim())) {
    return `${referIndividualBy} name must start with a letter and can only contain letters, numbers, spaces, and the following characters: . , ' -`;
  }

  // Duplicate names are now allowed since clients can be differentiated by date of birth
  return undefined;
};

const getClientDetailsByUUID = (uuid: string) => {
  return axiosInstance.get(`/client/uuid/${uuid}/`);
};

// Client sharing methods
const shareClient = async (
  clientId: number,
  userIds: number[],
): Promise<
  AxiosResponse<{ created: number; shares: TClientShare[]; already_shared?: number[]; invalid_users?: number[] }>
> => {
  return axiosInstance.post(`/client/${clientId}/share/`, { user_ids: userIds });
};

const getClientShares = async (clientId: number): Promise<AxiosResponse<TClientShare[]>> => {
  return axiosInstance.get(`/client/${clientId}/share/`);
};

const revokeClientShare = async (clientId: number, userId: number): Promise<AxiosResponse<{ message: string }>> => {
  return axiosInstance.delete(`/client/${clientId}/share/${userId}/`);
};

// Auto-share standing rules: share ALL of an owner's patients (current via
// backfill + future via fan-out). ownerId is only passed by group admins
// managing another member's rules.
const autoShareBase = (ownerId?: number) => (ownerId ? `/client/auto-share/owner/${ownerId}/` : "/client/auto-share/");

const getAutoShares = async (ownerId?: number): Promise<AxiosResponse<TAutoShareResponse>> => {
  return axiosInstance.get(autoShareBase(ownerId));
};

const createAutoShares = async (userIds: number[], ownerId?: number): Promise<AxiosResponse<TAutoShareResponse>> => {
  return axiosInstance.post(autoShareBase(ownerId), { user_ids: userIds });
};

const removeAutoShare = async (userId: number, ownerId?: number): Promise<AxiosResponse<{ message: string }>> => {
  return axiosInstance.delete(`${autoShareBase(ownerId)}${userId}/`);
};

export {
  createAutoShares,
  createClient,
  deleteClient,
  getAllClients,
  getAllDcmCodes,
  getAutoShares,
  getClientDetails,
  getClientDetailsByUUID,
  getClientsPaged,
  getClientShares,
  getUsersClientProgressSummary,
  removeAutoShare,
  revokeClientShare,
  shareClient,
  updateClient,
  validateClientName,
};
